// Close-request modal: shows every answered bid as a pickable row, with
// the current winner pre-selected. Confirms via the `close` transition,
// emitting a winner change first if the selection differs from the
// current `winnerBidId`.
//
// @aiInvariant Confirm is disabled until a bid is picked (the backend
//   `close` transition rejects with `WINNER_REQUIRED` otherwise). On a
//   server-side rejection the modal stays open and surfaces the error
//   inline so the user can recover.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { usePickWinner, useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import { carrierMarkText } from '../../carrierMark.js';
import { formatMoney } from '../../format.js';
import type { CarrierBid, CarrierBidQuote, RequestView, UserRef } from '../../types.js';

export interface CloseRequestModalProps {
  request: RequestView;
  me: UserRef;
}

interface Row {
  carrier: CarrierBid;
  bid: CarrierBidQuote;
}

export function CloseRequestModal({ request }: CloseRequestModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);
  const pickWinnerMutation = usePickWinner(request.id);
  const onClose = () => setModal(null);
  const rows: Row[] = [];
  for (const carrier of request.carriers) {
    if (carrier.state !== 'answered') continue;
    for (const bid of carrier.bids) {
      rows.push({ carrier, bid });
    }
  }
  const [picked, setPicked] = useState<string | null>(request.winnerBidId);
  const winnerRow = rows.find((r) => r.bid.id === picked) ?? null;
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function onConfirm(): Promise<void> {
    if (!picked) return;
    setSubmitError(null);
    try {
      if (request.winnerBidId !== picked) {
        await pickWinnerMutation.mutateAsync(picked);
      }
      await transitionMutation.mutateAsync({ key: 'close' });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:closeModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            icon="lock"
            disabled={!picked || transitionMutation.isPending || pickWinnerMutation.isPending}
            onClick={() => {
              void onConfirm();
            }}
          >
            {t('requests:closeModal.confirm')}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 10 }}>
            {winnerRow
              ? t('requests:closeModal.winnerText', { name: winnerRow.carrier.carrier.name })
              : t('requests:closeModal.pickWinner')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map(({ carrier, bid }) => {
              const selected = picked === bid.id;
              return (
                <button
                  key={bid.id}
                  type="button"
                  onClick={() => setPicked(bid.id)}
                  style={{
                    border: `1px solid ${selected ? 'var(--accent-1)' : 'var(--border-1)'}`,
                    borderRadius: 'var(--r-2)',
                    padding: 12,
                    background: selected ? 'var(--accent-1-subtle)' : 'var(--bg-0)',
                    color: 'var(--fg-1)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Icon name="crown" size={14} style={{ color: 'var(--accent-1-press)' }} />
                    <span style={{ fontWeight: 600 }}>{carrier.carrier.name}</span>
                    <span
                      aria-hidden="true"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--fg-3)',
                        padding: '2px 5px',
                        background: 'var(--bg-inset)',
                        borderRadius: 3,
                      }}
                    >
                      {carrierMarkText(carrier.carrier.name, carrier.carrier.code)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 24, fontSize: 13, flexWrap: 'wrap' }}>
                    <KV
                      label={t('requests:carriers.columns.price')}
                      value={`${formatMoney(bid.pricePerUnit, bid.currency)}/${bid.unit}`}
                    />
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>
                        {t('requests:carriers.columns.comment')}
                      </span>
                      <div style={{ fontSize: 12 }}>{bid.comment || '-'}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div
          style={{
            padding: 12,
            background: 'var(--status-warn-bg)',
            borderRadius: 'var(--r-2)',
            display: 'flex',
            gap: 10,
          }}
        >
          <Icon
            name="alert-triangle"
            size={16}
            style={{ color: 'var(--status-warn-fg)', flex: 'none', marginTop: 1 }}
          />
          <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
            {t('requests:closeModal.noWinner')}
          </div>
        </div>
      )}
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}

function KV({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div>
      <span style={{ color: 'var(--fg-3)', fontSize: 11 }}>{label}</span>
      <div style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value ?? '-'}</div>
    </div>
  );
}
